import React from 'react';
import { observer } from 'mobx-react';

import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { calcRightOffset } from 'libs/rendering/hooks';

import {
  GradientLabel,
  LegendWrapper,
  UnitsWrapper,
  GradientWrapper,
  Gradient,
  CutOffLevel,
  GradientLabels,
  GradientBox,
} from './legend.styled';

const legendSteps = 9;
const colorGrain = 3; // needs to be greater than 1

const Legend = observer(function _Legend(props) {
  const viewerControl = useViewerControl();

  const { viewer } = viewerControl;
  const { isDark } = props;
  const rightOffset = calcRightOffset();

  const settings = viewer?.settings;
  const currCamera = settings?.CurrentCamera();

  const currTessFieldType = currCamera?.GetCurrTessField();

  const fieldModeEnum = viewer?.module.TessFieldType;
  const thereIsTessField =
    currTessFieldType &&
    currCamera &&
    currTessFieldType !== fieldModeEnum.TessFieldNone
      ? currCamera.HasTessField(currTessFieldType)
      : false;
  const currTessIsManidoldTest =
    thereIsTessField && currTessFieldType === fieldModeEnum.TessFieldManifold;
  const legendVisible =
    currCamera?.IsInstance() && thereIsTessField && !currTessIsManidoldTest;

  const currGradient = currTessFieldType
    ? currCamera?.GetFieldGradient(currTessFieldType)
    : null;

  const units = currGradient?.unitScale;
  let cutOffPerc = -1.0;
  let visualCutOff = -1.0;
  // NOTE: continuous is always set to false. Left for future use...
  let continuous = false;
  if (currGradient) {
    visualCutOff = currGradient.GetVisualCutOff();
    if (visualCutOff > 0) {
      if (currGradient.isExtremeUpper) {
        cutOffPerc =
          visualCutOff * (100.0 / (currGradient.min - currGradient.max)) -
          (100 * currGradient.max) / (currGradient.min - currGradient.max);
      } else {
        cutOffPerc =
          visualCutOff * (100.0 / (currGradient.max - currGradient.min)) -
          (100 * currGradient.min) / (currGradient.max - currGradient.min);
      }
      continuous = false;

      // cap
      if (cutOffPerc > 100) {
        cutOffPerc = 100;
      } else if (cutOffPerc < 0) {
        cutOffPerc = 0;
      }
    }
  }

  const getGradientMin = () => {
    if (currCamera && currGradient) {
      return currGradient.visualMin * units;
    }
    return null;
  };

  const getGradientMax = () => {
    if (currCamera && currGradient) {
      return currGradient.visualMax * units;
    }
    return null;
  };

  const gradientLegendValues = () => {
    if (!legendVisible) return null;

    const min = getGradientMin();
    const max = getGradientMax();
    const span = max - min;
    const step = span / parseFloat(legendSteps);

    const values = [];
    const digits =
      currTessFieldType === fieldModeEnum?.TessFieldOverHang ||
      currTessFieldType === fieldModeEnum?.TessFieldSharpEdges
        ? 0
        : 1;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i <= legendSteps; i++) {
      let value;
      if (currGradient?.reversed) {
        value = Number((min + i * parseFloat(step)).toFixed(digits));
      } else {
        value = Number((max - i * parseFloat(step)).toFixed(digits));
      }

      values.push(
        <GradientLabel key={i} isDark={isDark}>{`${value}`}</GradientLabel>,
      );
    }
    return values;
  };

  const gradientLegendBoxes = () => {
    if (!legendVisible) return null;
    if (continuous) return null;
    if (!currGradient) return null;

    const boxCount = colorGrain * legendSteps;
    const min = getGradientMin();
    const max = getGradientMax();
    const span = max - min;
    const step = span / parseFloat(boxCount - 1);
    const calcStep = span / parseFloat(boxCount);

    const values = [];
    const digits =
      currTessFieldType === fieldModeEnum?.TessFieldOverHang ||
      currTessFieldType === fieldModeEnum?.TessFieldSharpEdges
        ? 0
        : 1;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < boxCount; i++) {
      let value;
      let colorNext = null;
      let perc = null;
      const scaledCutoff = Math.round(visualCutOff * units * 100) / 100;
      if (currGradient?.reversed) {
        value = Number((min + i * parseFloat(step)).toFixed(digits));
        const valueMin = Number(
          (min + i * parseFloat(calcStep)).toFixed(digits),
        );
        const valueMax = Number(
          (min + (i + 1) * parseFloat(calcStep)).toFixed(digits),
        );
        // check if cut-off inside division
        if (scaledCutoff > valueMin && scaledCutoff < valueMax) {
          const valueNext = Number(
            (min + (i + 1) * parseFloat(step)).toFixed(digits),
          );
          colorNext = currGradient.GetColor(valueNext / currGradient.unitScale);
          perc = (100.0 * (scaledCutoff - valueMin)) / calcStep;
        }
      } else {
        value = Number((max - i * parseFloat(step)).toFixed(digits));
        const valueMax = Number(
          (max - i * parseFloat(calcStep)).toFixed(digits),
        );
        const valueMin = Number(
          (max - (i + 1) * parseFloat(calcStep)).toFixed(digits),
        );
        if (scaledCutoff > valueMin && scaledCutoff < valueMax) {
          const valueNext = Number(
            (max - (i + 1) * parseFloat(step)).toFixed(digits),
          );
          colorNext = currGradient.GetColor(valueNext / currGradient.unitScale);
          perc = 100.0 - (100.0 * (scaledCutoff - valueMin)) / calcStep;
        }
      }

      const color = currGradient.GetColor(value / currGradient.unitScale);
      const css = `rgb(${255.0 * color.x},${255.0 * color.y},${
        255.0 * color.z
      })`;
      const cssNext =
        colorNext &&
        `rgb(${255.0 * colorNext.x},${255.0 * colorNext.y},${
          255.0 * colorNext.z
        })`;

      values.push(
        <GradientBox
          key={i}
          grain={colorGrain}
          color={css}
          colorNext={cssNext}
          perc={perc}
        />,
      );
    }
    return values;
  };

  // units
  const getFieldUnitLabel = () => {
    if (!currTessFieldType) return '';
    switch (currTessFieldType) {
      case fieldModeEnum?.TessFieldOverHang:
        return '[°]';
      case fieldModeEnum?.TessFieldSharpEdges:
        return '[°]';
      case fieldModeEnum?.TessFieldThickness:
        return '[mm]';
      default:
        break;
    }
    return '';
  };

  return (
    <LegendWrapper
      isDark={isDark}
      steps={legendSteps}
      right={rightOffset}
      visible={viewerControl?.viewerVisible && legendVisible}
    >
      <UnitsWrapper>{getFieldUnitLabel()}</UnitsWrapper>
      <GradientWrapper isDark={isDark} steps={legendSteps}>
        <Gradient continuous={continuous} cutOffPerc={cutOffPerc}>
          {gradientLegendBoxes()}
        </Gradient>
        <CutOffLevel cutOffPerc={cutOffPerc} />
      </GradientWrapper>
      <GradientLabels steps={legendSteps}>
        {gradientLegendValues()}
      </GradientLabels>
    </LegendWrapper>
  );
});

export default Legend;
